import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import { createPortal } from 'react-dom';
import {
  BALL_VALUES,
  COLORS,
  canPot,
  freeBallOffered,
  freeBallValue,
  isBallOnTable,
  isFreeBallPot,
  type BallColor,
  type ScoreState,
} from '@aiscoreboard/contracts';
import { BALL_GLOSS, BALL_SWATCH, PALE_BALLS as PALE, ballKey } from '../lib/balls';
import { TEAL } from '../lib/accent';
import { CrownIcon, FoulIcon, SwitchIcon, UndoIcon } from './Icons';
import { useT } from '../lib/i18n';
import { FoulFollowUp } from './FoulFollowUp';

interface Props {
  state: ScoreState;
  onPot: (color: BallColor, count: number, freeBall: boolean) => void;
  onFoul: (points: number) => void;
  onFreeBall: (awarded: boolean) => void;
  onSwitch: () => void;
  onUndo: () => void;
  onEndFrame: () => void;
  onFoulBack: () => void;
  endFramePending?: boolean;
  disabled?: boolean;
  foulDisabled?: boolean;
  foulFollowUp?: ReactNode;
}

/** The four ordinary foul awards. Anything else is a referee's decision. */
const FOULS = [4, 5, 6, 7];

/**
 * Everything a hand does, in one bar across the bottom of the board.
 *
 * It used to be three: a ball pad, a foul column beside it, and a row of
 * controls under both. Three blocks is three places to look for somebody whose
 * eyes are still on the table, and the two that were side by side made the
 * board's bottom half a grid of panels rather than a console. One bar, read
 * left to right, is the arrangement a scoreboard has had since they were built
 * out of switches: what happened (a ball), then what to do about it.
 *
 * Only the ball that is actually on can be tapped. Snooker alternates red,
 * colour, red, colour and then clears the colours in order, so at any moment
 * the set of legal pots is known exactly. An illegal ball is not a judgement
 * call the operator might want to overrule, it is a mis-tap u{2014}and a mis-tap
 * that goes in as a legal pot poisons the remaining points, the break, the
 * frame projection and the statistics, with nothing in the log to say it
 * happened. So illegal balls are shaded *and* dead.
 *
 * Two reds in one stroke are two taps on the red: red stays live straight
 * after a red, which is why the old "2 reds" and "3 reds" buttons are gone.
 * They were a second way to say a thing the red button could already say, and
 * a shortcut nobody reaches for is a button in the way of the ones they do.
 */
export function ActionBar({
  state,
  onPot,
  onFoul,
  onFreeBall,
  onSwitch,
  onUndo,
  onEndFrame,
  onFoulBack,
  endFramePending = false,
  disabled = false,
  foulDisabled,
  foulFollowUp,
}: Props) {
  const t = useT();
  // Nothing is left to pot, so nothing can be scored and nothing conceded.
  // The frame is over in everything but name, and the only honest next move is
  // to end it.
  const exhausted = state.pointsAvailable === 0;
  const locked = disabled || exhausted;
  const showFreeBall = freeBallOffered(state);

  return (
    <div data-foul-options={Boolean(foulFollowUp) || showFreeBall || undefined}
      className="action-bar hud-frame flex min-h-[clamp(5.6rem,15vh,9.4rem)] flex-none flex-wrap items-stretch gap-3 px-3 py-2 sm:gap-4 lg:flex-nowrap">
      {/* The balls are sized by the height they are given as well as by the
          width, because a circle has one dimension with two ceilings. Sizing
          from height alone clipped the red off the left edge of a wide short
          board; from width alone it overflows downwards on a tall one. */}
      <div className="ball-row flex min-h-0 min-w-0 basis-full flex-1 items-center justify-center gap-0 lg:basis-auto">
        {(['red', ...COLORS] as BallColor[]).map((color) => {
          // A free ball opens the pad to balls the order would otherwise
          // refuse. Which of the two a tap is, is not something the operator
          // has to decide: a ball that was legal anyway is potted normally,
          // and the nomination is spent only on one that was not.
          const onTable = isBallOnTable(state, color);
          const free = isFreeBallPot(state, color);
          const legal = onTable && canPot(state, color, 1);
          const gone = !onTable;
          const lit = legal && !exhausted;
          return (
            <button
              key={color}
              type="button"
              disabled={locked || !legal}
              onClick={() => onPot(color, 1, free)}
              title={
                free
                  ? t('match.freeBallWorth', { n: freeBallValue(state, color) })
                  : t(ballKey(color))
              }
              aria-label={`${t(ballKey(color))} ${
                free ? freeBallValue(state, color) : BALL_VALUES[color]
              }`}
              className="flex min-h-0 shrink-0 items-center justify-center
                         transition active:scale-95 disabled:cursor-not-allowed"
            >
              {/* The value is written across the ball, the way it is on a
                  practice set and on every televised pot graphic. It used to
                  be a caption underneath, on the argument that a number
                  stamped over a sphere flattens the highlight that makes the
                  disc read as round —true of a number laid flat over the
                  middle of it, but the highlight is up and to the left and the
                  figure is not. What the caption cost was worse: it pushed the
                  rack off the centre of its block and put the one thing being
                  read at the smallest size on the board. */}
              <span
                aria-hidden
                style={
                  lit
                    ? {
                        ...BALL_GLOSS,
                        filter: 'drop-shadow(0 0.05em 0.12em rgba(0,0,0,0.6)) brightness(1.06)',
                      }
                    : BALL_GLOSS
                }
                className={[
                  'ball-fit grid shrink-0 place-items-center rounded-full transition',
                  BALL_SWATCH[color],
                  // The ball that is on is the bright one. It used to wear a
                  // white collar as well, and a hard ring around a sphere is
                  // the one thing that stops it reading as a sphere —it
                  // flattens the silhouette back into a sticker. Light is the
                  // signal instead: a lit ball glows and the dead ones fall
                  // back.
                  lit ? '' : 'opacity-50 saturate-[0.7] brightness-[0.8]',
                  // A ball reachable only because a free ball was awarded is
                  // ringed in the same gold the award itself wears. It is the
                  // one place the rule against collars gives way: this ball is
                  // about to score something other than the figure printed on
                  // every other ball in the row, and that has to be visible
                  // before the tap, not explained after it.
                  free ? 'ring-2 ring-sys-yellow/80 ring-offset-0' : '',
                ].join(' ')}
              >
                {/* Sized in `em` off the ball's own diameter, so it is the
                    same fraction of the sphere on a 100px pad button as on a
                    phone. Ink is dark on the two pale balls and light on the
                    rest, with a shadow the opposite way, which is the only
                    thing that keeps a 7 legible over a yellow *and* over a
                    black. */}
                <span
                  className={`num text-[0.42em] font-semibold leading-none ${
                    PALE.has(color) ? 'text-black/65' : 'text-white/90'
                  } ${gone && color !== 'red' ? 'line-through' : ''}`}
                  style={{
                    textShadow: PALE.has(color)
                      ? '0 0.03em 0.05em rgba(255,255,255,0.45)'
                      : '0 0.03em 0.06em rgba(0,0,0,0.55)',
                  }}
                >
                  {/* What this tap will actually score. For a nominated free
                      ball that is the ball *on*, not the ball being struck —
                      the whole of the rule, printed where the hand is going. */}
                  {free ? freeBallValue(state, color) : BALL_VALUES[color]}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <span aria-hidden className="my-1 hidden w-px shrink-0 self-stretch bg-white/10 lg:block" />

      <div className="action-col flex w-full shrink-0 flex-wrap items-center justify-center gap-2 sm:gap-2.5 lg:w-auto lg:flex-nowrap lg:justify-start">
        {(foulFollowUp || showFreeBall) && (
          <div className="foul-followup-controls w-[clamp(8rem,18vw,11rem)]">
            {foulFollowUp ?? <FoulFollowUp state={state} disabled={disabled}
              onFreeBall={onFreeBall} onBack={onFoulBack} />}
          </div>
        )}
        <div className="flex flex-col items-center">
          <FoulButton onFoul={onFoul} disabled={exhausted || (foulDisabled ?? locked)} />
        </div>

        <ActionButton
          label={t('match.switchShort')}
          icon={<SwitchIcon className="h-[1.35rem] w-[1.35rem]" />}
          rgb="255,214,10"
          onClick={onSwitch}
          disabled={disabled}
        />
        {/* Undo is tinted in the left seat's teal — the same blue the at-table
            badge is painted in — rather than in the off-white it used to
            wear. Three keys lettered red, yellow and white read as a severity
            scale, which undo is not part of: it is the calmest thing on the
            bar and the one an operator reaches for after a mis-tap. A cool
            tint among two warm ones is what separates it. */}
        <ActionButton
          label={t('match.undo')}
          icon={<UndoIcon className="h-[1.35rem] w-[1.35rem]" />}
          rgb={TEAL.rgb}
          onClick={onUndo}
        />
        <ActionButton
          label={t('match.endFrame')}
          icon={<CrownIcon className="h-[1.35rem] w-[1.35rem]" />}
          rgb="255,214,10"
          onClick={onEndFrame}
          disabled={disabled || endFramePending}
          wide
          nowrap={!showFreeBall}
        />
      </div>
    </div>
  );
}

/**
 * The foul, and the four awards hidden behind it.
 *
 * Four permanent buttons were four of the largest targets on the board given
 * to the rarest thing that happens on it, sitting right beside the balls u{2014}and
 * a five-point foul pressed instead of a five-point blue is the one mistake
 * that costs points to *both* players at once. One button, then a choice,
 * makes a foul deliberate: nobody arrives at "6" without having meant "foul"
 * first.
 *
 * The awards open upward, because the bar is at the bottom of the board and a
 * menu that opens downward opens off the screen u{2014}and they are rendered into
 * the body rather than into the bar. The bar is a flex row inside a fixed-size
 * board, so a menu left inside it would be laid out as an eighth key and then
 * clipped by the first ancestor that scrolls.
 */
function FoulButton({ onFoul, disabled }: { onFoul: (points: number) => void; disabled: boolean }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState({ left: 0, bottom: 0 });
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !trigger.current) return;
    const r = trigger.current.getBoundingClientRect();
    setAt({ left: r.left + r.width / 2, bottom: window.innerHeight - r.top + 8 });
  }, [open]);

  // A menu that can only be closed by choosing from it is a trap: a foul
  // opened by mistake has to be as easy to abandon as it was to open.
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent): void => {
      const target = e.target as Node;
      if (!menu.current?.contains(target) && !trigger.current?.contains(target)) setOpen(false);
    };
    const escape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  return (
    <>
      {open &&
        createPortal(
          <div
            ref={menu}
            className="hud-frame fixed z-50 flex -translate-x-1/2 gap-1.5 p-2"
            style={{
              left: at.left,
              bottom: at.bottom,
              ['--hud-bright' as string]: 'rgba(255,105,97,0.5)',
              ['--hud-glow' as string]: 'transparent',
              ['--hud-fill' as string]: 'rgba(255,69,58,0.08)',
              ['--hud-c' as string]: '22px',
            }}
          >
            {FOULS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => {
                  setOpen(false);
                  onFoul(n);
                }}
                className="num h-11 w-11 rounded-2xl bg-sys-red/15 text-xl font-semibold
                           leading-none text-[#ff6b61] transition
                           ring-1 ring-inset ring-sys-red/40
                           hover:bg-sys-red/30 hover:text-white active:scale-95"
                title={t('match.foul', { n })}
              >
                {n}
              </button>
            ))}
          </div>,
          document.body,
        )}

      <ActionButton
        buttonRef={trigger}
        label={t('match.foulLabel')}
        icon={<FoulIcon className="h-[1.35rem] w-[1.35rem]" />}
        rgb="255,69,58"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        pressed={open}
      />
    </>
  );
}

/**
 * One console key: a glass lozenge with its symbol beside its word.
 *
 * It used to be a tall box with the icon stacked over the label, which is the
 * shape of a keypad u{2014}seven of them in a row made the bottom of the board look
 * like a calculator. A pill with the two side by side is the shape of a
 * *control*, it is the shape the reference draws, and at the same height it
 * gives the label a full line instead of a fifth of a box.
 *
 * The symbol is what is found by somebody looking at the table rather than the
 * screen; the word is what makes it unambiguous the first time, and what
 * survives translation when the symbol does not.
 */
function ActionButton({
  label,
  icon,
  rgb,
  onClick,
  disabled = false,
  pressed = false,
  buttonRef,
  wide = false,
  nowrap = false,
}: {
  label: string;
  icon: ReactNode;
  /** The key's tint: its rim, the wash behind the glass, and its lettering. */
  rgb: string;
  onClick: () => void;
  disabled?: boolean;
  pressed?: boolean;
  buttonRef?: Ref<HTMLButtonElement>;
  /** A key whose word is longer than the other three, and is given the room. */
  wide?: boolean;
  /** Forbid a second line: the word shrinks to fit the width instead. */
  nowrap?: boolean;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`hud-frame flex items-center justify-center gap-2 px-3 py-1.5 sm:py-2
                 transition hover:brightness-125 active:scale-95 disabled:cursor-not-allowed
                 disabled:opacity-35 sm:gap-2.5 sm:px-4 ${
                   wide
                     ? 'w-[clamp(6.1rem,18vw,9.2rem)]'
                     : 'w-[clamp(4.6rem,14vw,7.2rem)]'
                 }`}
      style={{
        // A pill: the radius is larger than the box can ever be, so the ends
        // stay perfectly round whatever the label does to the width.
        ['--hud-c' as string]: '999px',
        ['--hud-edge' as string]: '8px',
        ['--hud-bright' as string]: `rgba(${rgb},${pressed ? 0.7 : 0.22})`,
        ['--hud-glow' as string]: pressed ? `rgba(${rgb},0.12)` : 'transparent',
        ['--hud-fill' as string]: `rgba(${rgb},${pressed ? 0.16 : 0.03})`,
        color: `rgb(${rgb})`,
      }}
    >
      <span className="shrink-0 opacity-90">{icon}</span>
      <FitLabel text={label} nowrap={nowrap} />
    </button>
  );
}

/** How small a key's word is allowed to get before it stops being readable. */
const MIN_LABEL_PX = 8;

/**
 * A word set as large as its key allows, and no larger.
 *
 * The keys are all one width, on purpose. The row of balls beside them takes
 * whatever is left over, so a key that grew to fit its label would shrink
 * every ball on the board —and the board would then be laid out differently
 * in German than in Chinese, which is a redesign nobody asked for. "换手" is
 * two characters and "Spieler wechseln" is sixteen; no single width is right
 * for both, so the width is fixed and the word gives way instead.
 *
 * The word is allowed two things the key is not: it may wrap, and it may
 * shrink. The box it wraps inside is exactly as tall as the icon beside it, so
 * a label that takes two lines does not make its key any taller than a label
 * that takes one —which is what keeps a German bar the same height as a
 * Chinese one, down to the pixel. Type is stepped down half a point at a time
 * until the word fits that box in both directions, and stops at eight pixels,
 * below which nothing is worth setting.
 */
export function FitLabel({ text, nowrap = false }: { text: string; nowrap?: boolean }) {
  const box = useRef<HTMLSpanElement>(null);
  const word = useRef<HTMLSpanElement>(null);

  const fit = useCallback(() => {
    const outer = box.current;
    const inner = word.current;
    if (!outer || !inner) return;
    // Back to the size the class asks for, so what is measured is the label
    // itself rather than whatever the last pass shrank it to.
    inner.style.fontSize = '';
    let size = parseFloat(getComputedStyle(inner).fontSize);
    if (!size) return;
    const room = outer.getBoundingClientRect();
    const fits = (): boolean => {
      const r = inner.getBoundingClientRect();
      return r.width <= room.width + 0.1 && r.height <= room.height + 0.1;
    };
    while (!fits() && size > MIN_LABEL_PX) {
      size = Math.max(MIN_LABEL_PX, size - 0.5);
      inner.style.fontSize = `${size}px`;
    }
  }, []);

  useLayoutEffect(fit, [fit, text, nowrap]);

  // The key's width is fixed in `rem`, so it changes only when the breakpoint
  // or the root font size does —both silent, and both worth hearing about.
  // The webfont is the other silent change: Inter is self-hosted and arrives
  // after the first paint, and a word measured in the fallback face is
  // measured in the wrong one.
  useEffect(() => {
    const outer = box.current;
    let ro: ResizeObserver | undefined;
    if (outer && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(fit);
      ro.observe(outer);
    }
    void document.fonts?.ready.then(fit);
    return () => ro?.disconnect();
  }, [fit]);

  // Nudged three pixels left of true centre. The key is a pill: its right end
  // curves away and carries the rim's brightest highlight and the drop shadow
  // under it, so the last few pixels of the box are optically "wall" rather
  // than space. A word centred by measurement therefore sits right of centre
  // to the eye. The shift is a translate rather than padding on purpose —
  // padding would take the room out of the box the word is fitted to, and the
  // longer labels would answer by stepping down a half point.
  return (
    <span
      ref={box}
      className="grid h-6 min-w-0 flex-1 -translate-x-[3px] place-items-center overflow-hidden"
    >
      <span
        ref={word}
        className={`text-center text-[0.78rem] font-semibold uppercase
                    leading-[1.05] tracking-[0.09em] sm:text-sm sm:leading-[1.05] ${
                      nowrap ? 'w-max whitespace-nowrap' : 'w-full text-balance break-words'
                    }`}
      >
        {text}
      </span>
    </span>
  );
}
